import type { MaybePromise } from '@amazon/vinyl-util'
import {
    AbortError,
    bufferToByteStr,
    createDisposer,
    createShortUid,
    Deferred,
    logDebug,
    logVerbose,
    type RequestInitOptions,
    requestWithRetry,
} from '@amazon/vinyl-util'
import type { AuthTokenWithRefresh } from '@amzn/vinyl-amzn'
import { windowEvents } from '@/util/interaction'
import { openWindow } from '@/util/openWindow'
import { ctx } from '@/util/context'
import { IS_TAURI } from '@/util/platform'

const { target } = ctx('oAuth2')

export type OAuthOptions = {
    readonly clientId: string
    readonly authorizationEndpoint: string
    readonly tokenEndpoint: string
    readonly scopes: readonly string[]
    readonly clientSecret: MaybePromise<string>
    readonly redirectUri: string
}

/**
 * Grant type values used in dynamic client registration and auth requests.
 *
 * @see [Appendix A.10](https://tools.ietf.org/html/rfc6749#appendix-A.10)
 */
export enum GrantType {
    /**
     * Used for exchanging an authorization code for one or more tokens.
     */
    AUTHORIZATION_CODE = 'authorization_code',

    /**
     * Used when obtaining an access token.
     */
    IMPLICIT = 'implicit',

    /**
     * Used when exchanging a refresh token for a new token.
     */
    REFRESH_TOKEN = 'refresh_token',

    /**
     * Used for client credentials flow.
     */
    CLIENT_CREDENTIALS = 'client_credentials',
}

function base64UrlEncode(str: string) {
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Hash the code verifier to generate the code challenge
 * @param verifier
 */
async function generateCodeChallenge(verifier: string) {
    const encoder = new TextEncoder()
    const data = encoder.encode(verifier)
    const hash = await crypto.subtle.digest('SHA-256', data)
    const base64Hash = bufferToByteStr(hash)
    return base64UrlEncode(base64Hash)
}

export async function createAuthorizeUrl(
    options: OAuthOptions
): Promise<{ readonly codeVerifier: string; readonly state: string; readonly url: string }> {
    const codeVerifier = createShortUid()
    const codeChallenge = await generateCodeChallenge(codeVerifier)
    // `state` is a fresh random nonce distinct from the PKCE `codeVerifier`
    // (the verifier is a secret that must never leave this tab; `state` is
    // reflected through LWA and lands in the redirect URL). It doubles as the
    // per-login key for the opener-independent result channel below and as the
    // CSRF nonce the app validates on receipt.
    const state = createShortUid()
    const params = new URLSearchParams({
        client_id: options.clientId,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        redirect_uri: options.redirectUri,
        response_type: 'code',
        scope: options.scopes.join(' '),
        state,
    })
    return {
        codeVerifier,
        state,
        url: `${options.authorizationEndpoint}?${params.toString()}`,
    }
}

/**
 * Name of the origin-scoped channel (BroadcastChannel + `localStorage` key)
 * that carries the auth result back from `login.html`. Keyed on the per-login
 * `state` nonce so a result can only satisfy the login that minted it. This
 * derivation is intentionally duplicated verbatim in `html/login.html` (a
 * static, un-bundled page that can't import this module) — keep the two in sync.
 */
export function authResultChannelName(state: string): string {
    return `music-auth-result:${state}`
}

/** Shape `login.html` posts on the result channel. */
interface AuthResultMessage {
    readonly state: string
    readonly code: string | null
}

function isAuthResult(data: unknown, expectedState: string): data is AuthResultMessage {
    return (
        typeof data === 'object' &&
        data !== null &&
        'state' in data &&
        (data as { state: unknown }).state === expectedState
    )
}

export async function oAuthAuthorize(options: OAuthOptions): Promise<string> {
    logDebug(target, 'oAuthAuthorize')
    const { codeVerifier, state, url } = await createAuthorizeUrl(options)
    sessionStorage.setItem('codeVerifier', codeVerifier)

    if (IS_TAURI) {
        // Open the auth URL in a Tauri-owned child WebviewWindow. The Rust
        // `open_auth_popup` command intercepts the redirect to `redirectUri`
        // before it loads, parses the `code` from the query string, and emits
        // it back on `amp:auth-code`. This avoids registering a custom
        // URL scheme — on macOS that scheme would route the OAuth bounce to
        // whichever installed copy of Amp.app Launch Services picked,
        // not the running dev/release process.
        //
        // The @tauri-apps/* imports are dynamic so the Tauri runtime ends up
        // in its own lazily-loaded chunk and stays out of the web entry.
        const [{ invoke }, { listen }] = await Promise.all([
            import('@tauri-apps/api/core'),
            import('@tauri-apps/api/event'),
        ])
        const authTokenPromise = new Deferred<string>()
        const unlisten = await listen<{ code: string | null }>('amp:auth-code', (event) => {
            unlisten()
            if (event.payload.code) {
                authTokenPromise.resolve(event.payload.code)
            } else {
                authTokenPromise.reject(new AbortError())
            }
        })
        try {
            await invoke('open_auth_popup', {
                authUrl: url,
                redirectUrl: options.redirectUri,
            })
        } catch (e) {
            unlisten()
            throw e
        }
        return authTokenPromise
    }

    const popup = await openWindow(Promise.resolve(url), 'authPopup', 'width=400,height=600')
    if (!popup) throw new Error('auth popup could not be opened')

    return awaitAuthResult(state, popup)
}

/**
 * Resolve the LWA authorization `code` for a popup login WITHOUT depending on
 * `window.opener` surviving.
 *
 * When LWA interposes a CAPTCHA (or any other cross-origin step) the popup
 * navigates cross-origin, which severs `window.opener` — so by the time LWA
 * lands back on our same-origin `login.html`, `opener.postMessage` has nowhere
 * to post and the user is stranded. The robust postback path is therefore an
 * ORIGIN-scoped channel keyed on the per-login `state` nonce, which login.html
 * and the app both open by name: it survives opener loss because it is bound to
 * the origin, not to the popup's opener link.
 *
 * Delivery paths, in order of preference — the FIRST to arrive wins and the
 * rest are torn down:
 *  1. `window.opener.postMessage` (legacy fast-path) — still works when the
 *     opener wasn't severed; validated against `state` like every other path.
 *  2. `BroadcastChannel(authResultChannelName(state))` — the primary path.
 *  3. `localStorage[authResultChannelName(state)]` + a `storage` event — the
 *     fallback for engines without BroadcastChannel (fires cross-window,
 *     same-origin). Also covers the "result was written while we weren't
 *     listening" race: we read the key once on setup.
 *
 * Every path validates the nonce (a result for a `state` we didn't mint is
 * ignored) and, for `postMessage`, the origin (must match ours). The `code` is
 * never logged. If the popup is closed with no result the login rejects with an
 * `AbortError` so the caller can reset to a signed-out, retryable state rather
 * than hanging forever.
 */
function awaitAuthResult(state: string, popup: WindowProxy): Promise<string> {
    const authTokenPromise = new Deferred<string>()
    const { add, dispose } = createDisposer()
    const channelName = authResultChannelName(state)

    // login.html writes the result under `channelName` for the storage
    // fallback; clear it on teardown so a successful login (resolved via any
    // path) doesn't leave the code sitting in localStorage.
    const clearStored = () => {
        try {
            localStorage.removeItem(channelName)
        } catch {
            // storage unavailable — nothing to clear.
        }
    }
    const resolve = (code: string) => {
        dispose()
        clearStored()
        popup.close()
        authTokenPromise.resolve(code)
    }
    const reject = () => {
        dispose()
        clearStored()
        popup.close()
        authTokenPromise.reject(new AbortError())
    }

    // Accept a validated result from any transport. `null` code = LWA reported
    // no code (user cancelled / denied), which we surface as an abort.
    const accept = (data: unknown) => {
        if (!isAuthResult(data, state)) return
        logVerbose(target, 'auth result received', { hasCode: data.code !== null })
        if (data.code) resolve(data.code)
        else reject()
    }

    // 1. Opener fast-path. Validate the message origin against ours so a
    // cross-origin frame can't inject a result; login.html is same-origin.
    add(
        windowEvents.on('message', (event) => {
            if (event.origin && event.origin !== location.origin) return
            accept(event.data)
        })
    )

    // 2. Primary opener-independent path.
    if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel(channelName)
        channel.onmessage = (event) => accept(event.data)
        add(() => channel.close())
    }

    // 3. Storage fallback: read any already-written result once, then listen
    // for the cross-window `storage` event. login.html writes JSON under the
    // channel-name key; we clear it once consumed so it can't replay.
    const readStored = () => {
        let raw: string | null
        try {
            raw = localStorage.getItem(channelName)
        } catch {
            return // storage unavailable — rely on the other paths.
        }
        if (raw === null) return
        clearStored()
        try {
            accept(JSON.parse(raw))
        } catch {
            // A corrupt value is not a valid result — ignore it and keep
            // waiting for a well-formed one on another path.
        }
    }
    add(
        windowEvents.on('storage', (event) => {
            if (event.key === channelName) readStored()
        })
    )
    readStored()

    // Cordova InAppBrowser path: the popup ref emits `loadstart` on every
    // navigation (including the redirect back to `redirectUri`) and can't
    // postMessage, so read the `code` straight off the redirect URL. No-op in a
    // normal browser, where a cross-origin popup fires no `loadstart` we can see.
    if ('cordova' in global) {
        const loadStartHandler = (event: Event) => {
            const rawUrl = (event as { url?: string }).url
            if (!rawUrl) return
            // `new URL('')` throws rather than yielding an unparsable URL, so a
            // missing/malformed navigation URL must be guarded explicitly.
            let code: string | null
            try {
                code = new URL(rawUrl).searchParams.get('code')
            } catch {
                return
            }
            if (code) resolve(code)
        }
        popup.addEventListener('loadstart', loadStartHandler)
        add(() => popup.removeEventListener('loadstart', loadStartHandler))
    }

    // Popup dismissed without ever delivering a result → abort so the caller
    // resets rather than hanging. Poll because a cross-origin popup fires no
    // `close` event we can subscribe to.
    const closePoll = setInterval(() => {
        if (popup.closed) reject()
    }, 500)
    add(() => clearInterval(closePoll))

    return authTokenPromise
}

const commonInitOptions: RequestInitOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
}

/**
 * Exchange authorization code for access token
 */
export async function exchangeCodeForToken(
    options: OAuthOptions,
    code: string
): Promise<AuthTokenWithRefresh> {
    logDebug(target, 'exchangeCodeForToken')
    const codeVerifier = sessionStorage.getItem('codeVerifier')
    if (!codeVerifier) throw new Error('codeVerifier not set')
    const params = new URLSearchParams({
        code,
        client_id: options.clientId,
        client_secret: await options.clientSecret,
        redirect_uri: options.redirectUri,
        code_verifier: codeVerifier,
        grant_type: GrantType.AUTHORIZATION_CODE,
    })
    const response = await requestWithRetry(options.tokenEndpoint, {
        ...commonInitOptions,
        body: params,
    })
    const tokenData = await response.json()
    return tokenWithRefreshToAuthToken(tokenData)
}

export async function refreshToken(
    options: OAuthOptions,
    refreshToken: string
): Promise<AuthTokenWithRefresh> {
    logDebug(target, 'refreshToken')
    const params = new URLSearchParams({
        grant_type: GrantType.REFRESH_TOKEN,
        refresh_token: refreshToken,
        client_id: options.clientId,
        client_secret: await options.clientSecret,
    })
    const response = await requestWithRetry(options.tokenEndpoint, {
        ...commonInitOptions,
        body: params,
    })
    const tokenData = await response.json()
    return tokenWithRefreshToAuthToken(tokenData)
}

/** Raw OAuth token endpoint response (snake_case wire shape) consumed below. */
interface OAuthTokenResponse {
    readonly access_token: string
    readonly expires_in: string | number
    readonly refresh_token: string
}

/**
 * Converts a TokenWithRefresh object from Amazon auth to have an expiry timestamp instead of a relative duration.
 *
 * @param token
 */
function tokenWithRefreshToAuthToken(token: OAuthTokenResponse): AuthTokenWithRefresh {
    return {
        accessToken: token.access_token,
        expiresAt: parseInt(String(token.expires_in)) * 1000 + Date.now(),
        refreshToken: token.refresh_token,
    }
}
